import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import DataTable from '../../components/Shared/DataTable';
import { superAdminNavItems } from '../../constants/navigation';
import EmptyState from '../../components/Shared/EmptyState';

const STATUS_COLORS = {
  unused: 'bg-green-100 text-green-800',
  used: 'bg-blue-100 text-blue-800',
  expired: 'bg-yellow-100 text-yellow-800',
  revoked: 'bg-red-100 text-red-800',
};

const CodesPage = () => {
  const { currentUser } = useAuth();
  const [codes, setCodes] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/super-admin/codes?page=${currentPage}&per_page=20`;
      if (typeFilter) url += `&type=${typeFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const data = await apiRequest(url, 'GET');
      setCodes(data.codes || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter, statusFilter, searchQuery]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleRevoke = async (codeId, codeStr) => {
    if (!window.confirm(`Revoke code ${codeStr}? This cannot be undone.`)) return;
    try {
      await apiRequest(`/api/super-admin/codes/${codeId}/revoke`, 'PUT');
      fetchCodes();
    } catch (err) {
      alert(`Failed to revoke: ${err.message}`);
    }
  };

  const codeColumns = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (c) => (
        <button onClick={() => { navigator.clipboard.writeText(c.code); }}
          className="font-mono text-sm text-purple-600 hover:text-purple-800" title="Click to copy">
          {c.code}
        </button>
      ),
    },
    {
      key: 'code_type',
      label: 'Type',
      sortable: true,
      render: (c) => <span className="capitalize font-medium">{c.code_type}</span>,
    },
    {
      key: 'school_name',
      label: 'School',
      render: (c) => <span className="text-sm">{c.school_name || 'N/A'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (c) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-800'}`}>
          {c.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (c) => <span className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : 'N/A'}</span>,
    },
    {
      key: 'expires_at',
      label: 'Expires',
      render: (c) => <span className="text-xs text-gray-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (c) => {
        if (c.status === 'unused') {
          return (
            <button onClick={() => handleRevoke(c.id, c.code)}
              className="text-red-600 hover:text-red-800 text-sm font-medium">
              Revoke
            </button>
          );
        }
        return null;
      },
    },
  ];

  if (loading && codes.length === 0) {
    return (
      <DashboardLayout title="Activation Codes" navItems={superAdminNavItems} userRole="super-admin" userName={currentUser?.email || 'Admin'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading codes...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Activation Codes" navItems={superAdminNavItems} userRole="super-admin" userName={currentUser?.email || 'Admin'}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Activation Codes</h2>
        <span className="text-sm text-gray-500">{pagination?.total || 0} total codes</span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">Error: {error}</div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input type="text" placeholder="Search codes..." value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" />
        <select value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm">
          <option value="">All Types</option>
          <option value="master">Master</option>
          <option value="teacher">Teacher</option>
          <option value="student">Student</option>
        </select>
        <select value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm">
          <option value="">All Status</option>
          <option value="unused">Unused</option>
          <option value="used">Used</option>
          <option value="expired">Expired</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>

      {!loading && codes.length === 0 && !searchQuery && !typeFilter && !statusFilter ? (
        <EmptyState
          icon="&#128273;"
          title="No Activation Codes"
          description="No activation codes found. Create a school to generate master codes."
        />
      ) : (
        <DataTable
          columns={codeColumns}
          data={codes}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          emptyMessage="No activation codes found matching your filters."
        />
      )}
    </DashboardLayout>
  );
};

export default CodesPage;
